import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppLayout } from "./components/Layout/AppLayout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { WorkspaceProvider } from "./contexts/WorkspaceContext";
import Agents from "./pages/Agents";
import AgentEditor from "./pages/AgentEditor";
import KnowledgeBase from "./pages/KnowledgeBase";
import PhoneNumbers from "./pages/PhoneNumbers";
import BatchCall from "./pages/BatchCall";
import CampaignDetails from "./pages/CampaignDetails";
import CallHistory from "./pages/CallHistory";
import ChatHistory from "./pages/ChatHistory";
import Analytics from "./pages/Analytics";
import Settings from "./pages/Settings";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";
import VoiceTest from "./pages/VoiceTest";
import AcceptInvitation from "./pages/AcceptInvitation";
import Billing from "./pages/Billing";
import { WorkspaceRedirect } from "./components/WorkspaceRedirect";
import { WorkspaceIdGuard } from "./components/WorkspaceIdGuard";
import appVersion from "../../version.json";

console.log(`App version: ${appVersion.version}`);

const queryClient = new QueryClient();

// Capture React render errors in Sentry without showing anything to the user.
const App = () => (
  <Sentry.ErrorBoundary fallback={() => null}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
          <Route path="/auth" element={<Auth />} />
          <Route path="/invite/:token" element={<AcceptInvitation />} />
          
          {/* Root route redirects to default workspace */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <WorkspaceProvider>
                  <WorkspaceRedirect />
                </WorkspaceProvider>
              </ProtectedRoute>
            }
          />

          <Route
            path="/:workspaceId"
            element={
              <WorkspaceIdGuard>
                <ProtectedRoute>
                  <WorkspaceProvider>
                    <AppLayout />
                  </WorkspaceProvider>
                </ProtectedRoute>
              </WorkspaceIdGuard>
            }
          >
            <Route index element={<Agents />} />
            <Route path="agents" element={<Agents />} />
            <Route path="agents/:id" element={<AgentEditor />} />
            <Route path="knowledge" element={<KnowledgeBase />} />
            <Route path="phone-numbers" element={<PhoneNumbers />} />
            <Route path="batch-call" element={<BatchCall />} />
            <Route path="batch-call/:campaignId" element={<CampaignDetails />} />
            <Route path="call-history" element={<CallHistory />} />
            <Route path="chat-history" element={<ChatHistory />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="billing" element={<Billing />} />
            <Route path="settings" element={<Settings />} />
          </Route>
          <Route path="/voice-test/:agentId" element={<VoiceTest />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
